// Mirrors the server's variant picker
// (server/src/Controllers/notification-setting/resolver/select-variant.ts) so
// the appointment modal can display the channels that will actually fire for
// a given service/location. Audience handling is simplified — the client does
// not know the customer's history-derived audience, so we treat audience as
// non-discriminating: variants with `audience !== "all"` are still considered
// candidates and the most-specific one wins by service/location specificity.
// In practice, tenants without audience-targeted variants get the exact same
// pick as the dispatcher; tenants with audience-targeted variants may see a
// slightly optimistic icon set (the most-specific variant on other axes).

export type VariantChannel = {
  channel: "sms" | "viber" | "whatsapp" | "email";
  fallbackOrder: number;
  isActive: boolean;
  // Localized content rows for this channel (one per configured language). A
  // channel only fires when at least one row has a non-empty body — see
  // `getActiveChannelsForVariant`. Optional because an older deployed server
  // (version skew) may omit it from the payload; treat a missing list as
  // "no usable content".
  localized?: { body: string }[];
};

export type VariantCandidate = {
  id: number;
  audience: "all" | "newCustomer" | "regularCustomer";
  allServices: boolean;
  allSubscriptions: boolean;
  allLocations: boolean;
  isDefault: boolean;
  channels: VariantChannel[];
  services: { serviceId: number }[];
  subscriptions: { subscriptionId: number }[];
  locations: { locationId: number }[];
};

type ServicePickInput = {
  serviceId: number | null;
  locationId: number | null;
};

function matchesServiceAxes(
  v: VariantCandidate,
  { serviceId, locationId }: ServicePickInput,
): boolean {
  const serviceMatch =
    serviceId === null ||
    v.allServices ||
    v.services.some((s) => s.serviceId === serviceId);
  const locationMatch =
    locationId === null ||
    v.allLocations ||
    v.locations.some((l) => l.locationId === locationId);
  return serviceMatch && locationMatch;
}

function pickMostSpecific(candidates: VariantCandidate[]): VariantCandidate {
  const scored = candidates.map((v) => ({
    v,
    service: v.allServices ? 0 : 1,
    location: v.allLocations ? 0 : 1,
    audience: v.audience === "all" ? 0 : 1,
  }));

  scored.sort((a, b) => {
    const aCount = a.service + a.location + a.audience;
    const bCount = b.service + b.location + b.audience;
    if (aCount !== bCount) return bCount - aCount;
    if (a.service !== b.service) return b.service - a.service;
    if (a.location !== b.location) return b.location - a.location;
    return b.audience - a.audience;
  });

  return scored[0]!.v;
}

export function pickAppointmentVariant(
  variants: VariantCandidate[],
  input: ServicePickInput,
): VariantCandidate | null {
  if (variants.length === 0) return null;
  const candidates = variants.filter(
    (v) => !v.isDefault && matchesServiceAxes(v, input),
  );
  if (candidates.length === 0) {
    return variants.find((v) => v.isDefault) ?? variants[0] ?? null;
  }
  return pickMostSpecific(candidates);
}

export function pickSubscriptionVariant(
  variants: VariantCandidate[],
  subscriptionId: number | null,
): VariantCandidate | null {
  if (variants.length === 0) return null;
  if (subscriptionId !== null) {
    const specific = variants.find(
      (v) =>
        !v.isDefault &&
        !v.allSubscriptions &&
        v.subscriptions.some((s) => s.subscriptionId === subscriptionId),
    );
    if (specific) return specific;
  }
  return variants.find((v) => v.isDefault) ?? variants[0] ?? null;
}

// A channel fires only when it is active AND has usable content: at least one
// localized row with a non-empty body. This mirrors the server resolver
// (`select-content.ts` `hasUsableBody` / `pickLocalizedContent`), which skips a
// channel that is enabled but has no body in any locale — so the modal must not
// show a toggle (or icon) for it.
export function getActiveChannelsForVariant(
  variant: VariantCandidate | null,
): VariantChannel[] {
  if (!variant) return [];
  return [...variant.channels]
    .filter(
      (c) =>
        c.isActive &&
        (c.localized?.some((l) => l.body.trim().length > 0) ?? false),
    )
    .sort((a, b) => a.fallbackOrder - b.fallbackOrder);
}
